/**
 * Dashboard Layout - Mobile Responsive
 */
import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, type Organization } from '../lib/api';
import { OrganizationSelector } from './OrganizationSelector';

interface LayoutProps {
  children: ReactNode;
}

// Navigation items with access control
const getNavItems = (isGlobalAdmin: boolean) => {
  const baseItems = [
    { path: '/', label: 'Dashboard', icon: '📊' },
    { path: '/receipts', label: 'Receipts', icon: '🧾' },
    { path: '/income', label: 'Income', icon: '💰' },
    { path: '/reports', label: 'Reports', icon: '📈' },
    { path: '/income-audit', label: 'Audit Log', icon: '📋' },
    { path: '/users', label: 'Users', icon: '👥' },
    { path: '/settings', label: 'Settings', icon: '⚙️' },
  ];

  // Only global admins can see Organizations menu
  if (isGlobalAdmin) {
    baseItems.splice(5, 0, { path: '/organizations', label: 'Organizations', icon: '🏢' });
  }

  return baseItems;
};

export function Layout({ children }: LayoutProps) {
  const { user, logout, currentOrganizationId, setCurrentOrganizationId, isGlobalAdmin } = useAuth();
  const location = useLocation();
  const navItems = getNavItems(isGlobalAdmin);
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [totalOrganizations, setTotalOrganizations] = useState(0);
  const [initialOrganization, setInitialOrganization] = useState<Organization | null>(null);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(true);

  useEffect(() => {
    // Fetch organization count and auto-select first org if needed
    const fetchOrganizationInfo = async () => {
      try {
        // Only fetch first page to get count and auto-select
        const result = await api.getOrganizations({ limit: 10 });
        if (result.success && result.data) {
          setTotalOrganizations(result.data.pagination.total);
          // Auto-select first org if none selected
          if (!currentOrganizationId && result.data.organizations.length > 0) {
            const firstOrg = result.data.organizations[0];
            setCurrentOrganizationId(firstOrg.id);
            setInitialOrganization(firstOrg);
          } else if (currentOrganizationId) {
            // Find current org in results or fetch it
            const currentOrg = result.data.organizations.find(o => o.id === currentOrganizationId);
            if (currentOrg) {
              setInitialOrganization(currentOrg);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
      } finally {
        setIsLoadingOrgs(false);
      }
    };

    fetchOrganizationInfo();
  }, [currentOrganizationId, setCurrentOrganizationId]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white shadow-sm z-40 flex items-center justify-between px-4">
        <h1 className="text-lg font-bold text-blue-600">ND Expense</h1>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar - Desktop fixed, Mobile slide-in */}
      <div
        className={`fixed inset-y-0 left-0 w-64 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b">
          <h1 className="text-xl font-bold text-blue-600">ND Expense</h1>
        </div>

        {/* Organization Selector */}
        {!isLoadingOrgs && (
          <OrganizationSelector
            totalOrganizations={totalOrganizations}
            initialOrganization={initialOrganization}
          />
        )}
        {isLoadingOrgs && (
          <div className="px-4 py-3 border-b">
            <label className="block text-xs font-medium text-gray-500 mb-1">Organization</label>
            <div className="h-9 bg-gray-100 rounded-lg animate-pulse" />
          </div>
        )}

        {/* Navigation */}
        <nav className="p-4">
          <ul className="space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={closeMobileMenu}
                    className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className="mr-3">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">{user?.username}</p>
              <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                isGlobalAdmin
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-blue-100 text-blue-700'
              }`}>
                {isGlobalAdmin ? 'Global Admin' : 'Org Admin'}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="text-sm text-red-600 hover:text-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64 min-h-screen pt-14 lg:pt-0">
        {children}
      </div>
    </div>
  );
}
